import * as ramda from 'ramda';

import {Routing} from 'nutshell-core/routing';
import type {CoreEntityType, SavedFilter, SavedListType} from 'nutshell-core/types';

import type {GetProspectorSavedFilters_prospectorSavedFilters as ProspectorSavedFilter} from 'nutshell-graphql-types';

import {getReduxList} from '../ui/entity-list/entity-list-sidebar/helpers';
import type {ReduxSavedFilters} from '../ui/entity-list/entity-list-sidebar/types';

/**
 * Given a filter string, strip out the saved list parameter
 * @param  {string} value  query string
 * @return {string}        query string without `list` param
 */
export function removeListParamFromValue(value: string) {
    const valueDeparamed = Routing.deparam(value);

    if (valueDeparamed.list) {
        delete valueDeparamed.list;
    }

    return Routing.param(valueDeparamed);
}

/**
 * Given a filter string, strip out the saved view parameter
 * @param  {string} value  query string
 * @return {string}        query string without `view` param
 */
function removeViewParamFromValue(value: string) {
    const valueDeparamed = Routing.deparam(value);

    if (valueDeparamed.view) {
        delete valueDeparamed.view;
    }

    return Routing.param(valueDeparamed);
}

/**
 * Remove query parameters which we do not want to consider when checking for
 * dirty state.
 * @param  {string} value query string
 * @return {string}       query string without unsavable params
 */
function sanitizeQueryString(value: string) {
    return removeListParamFromValue(removeViewParamFromValue(value));
}

/**
 * Mostly a wrapper around isSavedListDirty that is specifically meant to be used for determining
 * if our All [companies/people/leads] list is dirty. In this case, we will have no saved list ID
 * in the URL and thus no activeSavedList, so we use the default lists for our comparison based
 * on the current entityType.
 *
 * @param {string} queryString               The current search query string
 * @param {CoreEntityType} entityType        The entity type of the saved list
 * @param {ReduxSavedFilters} savedFilters   The list of saved filters from our Redux state
 * @returns {boolean}                        Whether the saved list is modified or not
 */
export function isAllEntitiesListDirty(
    queryString: string,
    entityType: CoreEntityType,
    savedFilters: ReduxSavedFilters
): boolean {
    const savedList = getReduxList(entityType, savedFilters);

    return isSavedListDirty(queryString, savedList, entityType);
}

/**
 * Check our current list's value with the saved value of the list.
 *
 * This method is slightly complicated because the URL doesn't match up
 * exactly with the saved value of the list, since we purposefully remove
 * the list id when saving to the DB, and the immutable leads lists don't
 * include the default report params, so we can't just simply compare
 * the url with the saved list's value.
 *
 * @param  {string}  queryString   - The current location.search string
 * @param  {Object}  savedList     - A currently active saved list
 * @param  {entityType} entityType - The entity type of the saved list
 *
 * @return {boolean} Is this report dirty?
 */
export function isSavedListDirty(
    queryString: string,
    savedList: SavedFilter<SavedListType> | null | undefined,
    entityType: CoreEntityType
) {
    // On initial load, the Redux store may not yet be populated with our
    // default lists, so we may not have one and should early return in that case
    if (!savedList) {
        return false;
    }

    const currentValue = queryString.replace(/^\?/, '');
    const current = Routing.deparam(sanitizeQueryString(currentValue));
    const view = Routing.deparam(queryString).view;

    const saved = Routing.deparam(sanitizeQueryString(savedList.value));

    // Need to add default report params to immutable saved lists, because
    // they don't include them.
    if (savedList.isImmutable && savedList.reportType === 'entity_list_leads') {
        saved.report = getDefaultReportParams();
    }

    if (entityType === 'leads' && view === 'board') {
        if (!saved.filter || !saved.filter.stageset) {
            if (current.filter && current.filter.stageset) {
                delete current.filter.stageset;
                if (Object.keys(current.filter).length === 0) {
                    delete current.filter;
                }
            }
        }
        delete current.sort;
        delete saved.sort;
    }

    return !ramda.equals(current, saved);
}

/**
 * Returns the default report parameters that are added client-side to leads lists.
 *
 * This is a bit of a stopgap to allow comparing immutable filter values against
 * the url bar.
 *
 * @return {Object}     Default report parameters
 */
function getDefaultReportParams() {
    return {gap: 'w', pivotFacet: 'count', type: 'createdTime'};
}

/**
 * This is a simplified version of isSavedListDirty. It allows taking in a
 * generic saved filter, and compares the current URL with the saved filter's
 * filterString. It allows takes in a boolean isFiltering, which is true if the
 * user is currently filtering the list, as we do not wanna consider the list
 * dirty if the user is not filtering the list at all.
 *
 * Bonus: This also uses our graphql type instead of a custom type for the
 * saved filter.
 *
 * @param  {Object}  selectedFilter - A currently active saved list filter
 * @param  {string}  locationSearch - The current location.search string
 *
 * @return {boolean} Is this url dirty?
 */
export function getUrlIsDirty(
    selectedFilter: ProspectorSavedFilter | null | undefined,
    locationSearch: string | null | undefined,
    isFiltering: boolean
) {
    const current = Routing.deparam(locationSearch || '');
    const saved = selectedFilter ? Routing.deparam(selectedFilter.filterString) : {};

    return !ramda.equals(current, saved) && isFiltering;
}
